import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function RegisterPage() {
  const { signUp, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Info about who invited this person
  const [inviterName, setInviterName] = useState('');
  const [inviterLoading, setInviterLoading] = useState(!!inviteToken);

  useEffect(() => {
    if (!inviteToken) {
      setInviterLoading(false);
      return;
    }

    let cancelled = false;

    supabase
      .from('step6_team_members')
      .select('name, user_id')
      .eq('invite_token', inviteToken)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (data) {
          // Get the inviter's profile name
          supabase
            .from('step6_profiles')
            .select('full_name')
            .eq('id', data.user_id)
            .maybeSingle()
            .then(({ data: pData }) => {
              if (cancelled) return;
              setInviterName(pData?.full_name || 'STEP6 Lideri');
              setInviterLoading(false);
            });
        } else {
          setInviterLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [inviteToken]);

  if (loading || inviterLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080A10' }}>
        <div className="w-10 h-10 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (user) {
    navigate('/');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    setSubmitting(true);
    const result = await signUp(email, password, name, inviteToken || undefined);
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
    } else {
      setSuccess('Kayıt başarılı! E-posta adresine gönderilen doğrulama linkine tıkla, sonra giriş yap.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#080A10' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="https://public.readdy.ai/ai/img_res/bd011a02-ab2e-435b-9429-0067149db8ff.png"
              alt="STEP6"
              className="w-12 h-12 rounded-xl object-contain"
            />
            <span className="text-2xl font-bold text-white">STEP6 Network</span>
          </div>
          <p className="text-sm text-gray-500">Ekibini kur, network&apos;ünü büyüt</p>
        </div>

        {inviteToken && inviterName && (
          <div className="rounded-xl px-4 py-3 mb-4 flex items-center gap-3" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(34,197,94,0.15)' }}>
              <i className="ri-user-received-2-line text-green-400" />
            </div>
            <div>
              <p className="text-xs text-green-400 font-medium">Davetlisiniz!</p>
              <p className="text-xs text-gray-400"><span className="text-white font-semibold">{inviterName}</span> sizi STEP6 ekibine davet etti. Kayıt olunca otomatik olarak ekibe bağlanacaksınız.</p>
            </div>
          </div>
        )}

        <div className="rounded-2xl p-6 md:p-8" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <h2 className="text-lg font-bold text-white mb-6">{inviteToken ? 'Davetle Kayıt Ol' : 'Kayıt Ol'}</h2>

          <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
            <div>
              <label className="text-xs font-medium text-gray-400 mb-1.5 block">Ad Soyad</label>
              <input
                type="text"
                name="fullname"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ad Soyad"
                className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-400 mb-1.5 block">E-posta</label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="ornek@email.com"
                className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-400 mb-1.5 block">Şifre</label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="En az 6 karakter"
                className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              />
            </div>

            {error && (
              <div className="rounded-xl px-3 py-2.5 text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {success && (
              <div className="rounded-xl px-3 py-2.5 text-xs" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <p className="text-green-400">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Hesap oluşturuluyor...
                </span>
              ) : (
                'Kayıt Ol'
              )}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-6">
            Zaten hesabın var mı?{' '}
            <a href="/auth/login" className="text-amber-400 hover:text-amber-300 font-medium">
              Giriş Yap
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}